<template>
  <AppLayout pageTitle="Self Service">
    <div class="row">
      <div class="col-lg-8 mx-auto">
        <!-- Instructions -->
        <div class="card border-0 mb-4" style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
          <div class="card-body px-4 py-4">
            <div class="d-flex align-items-center mb-3">
              <div class="rounded-3 d-flex align-items-center justify-content-center me-3" style="width: 48px; height: 48px; background: linear-gradient(135deg, #ec4899, #f472b6);">
                <i class="ph-duotone ph-clipboard-text text-white f-s-22"></i>
              </div>
              <div>
                <h5 class="fw-bold mb-0" style="color: #0f172a;">Resolve Missing Payment</h5>
                <small class="text-muted">Submit M-Pesa SMS for unrecorded transactions</small>
              </div>
            </div>

            <div class="p-3 rounded-3 mb-3" style="background: #fefce8; border-left: 4px solid #f59e0b;">
              <p class="fw-semibold mb-2 f-s-13" style="color: #92400e;">
                <i class="ph-duotone ph-warning-circle me-1"></i> Use this form ONLY if:
              </p>
              <ul class="mb-0 f-s-13 text-muted ps-3">
                <li class="mb-1">You paid via M-Pesa and received a confirmation SMS</li>
                <li class="mb-1">You waited at least 10 minutes</li>
                <li>Your payment does NOT appear on your Transactions page</li>
              </ul>
            </div>

            <div class="p-3 rounded-3" style="background: #f0fdf4; border-left: 4px solid #10b981;">
              <p class="fw-semibold mb-1 f-s-13" style="color: #065f46;">
                <i class="ph-duotone ph-check-circle me-1"></i> What happens next:
              </p>
              <small class="text-muted">
                The system verifies your transaction code. If valid and not already recorded, it will be added immediately and your balance updated.
              </small>
            </div>
          </div>
        </div>

        <!-- Submission Form -->
        <div class="card border-0 mb-4" style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
          <div class="card-header bg-white border-0 pt-4 pb-2 px-4" style="border-radius: 14px 14px 0 0;">
            <h5 class="fw-bold mb-0" style="color: #0f172a;">
              <i class="ph-duotone ph-paper-plane-tilt me-2 text-primary"></i> Submit Transaction
            </h5>
          </div>
          <div class="card-body px-4 pb-4">
            <div v-if="errorMessage" class="alert border-0 mb-3" style="background: #fef2f2; color: #ef4444; border-radius: 12px;">
              <i class="ph-bold ph-warning-circle me-2"></i> {{ errorMessage }}
              <button type="button" class="btn-close float-end" @click="errorMessage = ''" style="font-size: 10px;"></button>
            </div>
            <div v-if="successMessage" class="alert border-0 mb-3" style="background: #ecfdf5; color: #10b981; border-radius: 12px;">
              <i class="ph-bold ph-check-circle me-2"></i> {{ successMessage }}
              <button type="button" class="btn-close float-end" @click="successMessage = ''" style="font-size: 10px;"></button>
            </div>

            <form @submit.prevent="submitTransaction">
              <div class="mb-3">
                <label class="form-label fw-medium f-s-13">M-Pesa Transaction Message <span class="text-danger">*</span></label>
                <textarea class="form-control" v-model="transactionMessage" rows="5" required
                          placeholder="Paste your complete M-Pesa SMS here..." :disabled="isSubmitting"
                          style="border-radius: 10px; padding: 12px 14px;"></textarea>
                <small class="form-text text-muted f-s-12">Copy and paste the entire SMS message from M-Pesa</small>
              </div>
              <div class="mb-3">
                <label class="form-label fw-medium f-s-13">Transaction Type <span class="text-danger">*</span></label>
                <select class="form-select" v-model="transactionType" required :disabled="isSubmitting" style="border-radius: 10px; padding: 10px 14px;">
                  <option value="" disabled>Select account type</option>
                  <option value="share_capital">Share Capital (SC)</option>
                  <option value="savings">Deposit Contribution (SV)</option>
                  <option value="land_installments">Plot Investments (LI)</option>
                </select>
              </div>
              <div class="mb-4">
                <label class="form-label fw-medium f-s-13">Additional Notes (Optional)</label>
                <textarea class="form-control" v-model="notes" rows="2"
                          placeholder="Any additional information..." :disabled="isSubmitting"
                          style="border-radius: 10px; padding: 10px 14px;"></textarea>
              </div>
              <div class="d-flex gap-2">
                <button type="submit" class="btn fw-semibold flex-grow-1" :disabled="isSubmitting || !transactionMessage || !transactionType"
                        style="background: linear-gradient(135deg, #0891b2, #06b6d4); color: white; border-radius: 10px; padding: 12px;">
                  <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
                  <i v-else class="ph-duotone ph-paper-plane-tilt me-2"></i>
                  {{ isSubmitting ? 'Processing...' : 'Submit Transaction' }}
                </button>
                <button type="button" class="btn btn-outline-secondary fw-semibold" @click="resetForm" :disabled="isSubmitting" style="border-radius: 10px; padding: 12px 20px;">
                  Clear
                </button>
              </div>
            </form>
          </div>
        </div>

        <!-- Recent Submissions -->
        <div class="card border-0" style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);" v-if="recentSubmissions.length > 0">
          <div class="card-header bg-white border-0 pt-4 pb-2 px-4" style="border-radius: 14px 14px 0 0;">
            <h6 class="fw-bold mb-0" style="color: #0f172a;">
              <i class="ph-duotone ph-clock-countdown me-2 text-muted"></i> Recent Submissions
            </h6>
          </div>
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover align-middle mb-0">
                <thead>
                  <tr style="background: #f8fafc;">
                    <th class="border-0 f-s-13 fw-semibold text-muted px-4 py-3">Date</th>
                    <th class="border-0 f-s-13 fw-semibold text-muted py-3">Code</th>
                    <th class="border-0 f-s-13 fw-semibold text-muted py-3">Amount</th>
                    <th class="border-0 f-s-13 fw-semibold text-muted py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="s in recentSubmissions" :key="s.id">
                    <td class="px-4 py-3 f-s-13 text-muted">{{ formatDate(s.created_at) }}</td>
                    <td class="py-3"><code class="f-s-12">{{ s.transaction_code }}</code></td>
                    <td class="py-3 fw-semibold f-s-13" :class="store.balancesHidden ? 'balance-blurred' : 'balance-visible'" style="color: #10b981;">KES {{ formatAmount(s.amount) }}</td>
                    <td class="py-3">
                      <span class="badge rounded-pill px-3 py-2 f-s-11" :class="getStatusBadgeClass(s.status)">
                        {{ s.status }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '@/components/AppLayout.vue';
import api from '@/services/axios.js';
import { useUserStore } from '@/store';

export default {
    name: 'PendingDeposits',
    components: { AppLayout },
    computed: {
        store() {
            return useUserStore();
        },
    },
    data() {
        return {
            transactionMessage: '',
            transactionType: '',
            notes: '',
            isSubmitting: false,
            errorMessage: '',
            successMessage: '',
            recentSubmissions: []
        };
    },
    mounted() {
        this.fetchRecentSubmissions();
    },
    methods: {
        async submitTransaction() {
            this.errorMessage = '';
            this.successMessage = '';

            try {
                if (!this.transactionMessage.trim()) {
                    this.errorMessage = 'Please paste your M-Pesa transaction message';
                    return;
                }

                if (!this.transactionType) {
                    this.errorMessage = 'Please select a transaction type';
                    return;
                }

                const store = useUserStore();
                const memberId = store.memberId;

                if (!memberId) {
                    this.errorMessage = 'User session not found. Please log in again.';
                    return;
                }

                this.isSubmitting = true;

                const response = await api.post(
                    '/api/pending-deposits/submit',
                    {
                        transaction_message: this.transactionMessage,
                        transaction_type: this.transactionType,
                        notes: this.notes,
                        member_id: memberId
                    }
                );

                // Handle both new transactions and already existing ones
                if (response.data.success || response.status === 200) {
                    this.successMessage = response.data.message;

                    // Show transaction details if provided
                    if (response.data.data) {
                        const data = response.data.data;
                        if (data.transaction_id) {
                            this.successMessage += ` Transaction ID: ${data.transaction_id}`;
                        }
                        if (data.account_number) {
                            this.successMessage += ` | Account: ${data.account_number}`;
                        }
                    }

                    this.resetForm();
                    this.fetchRecentSubmissions();

                    // Scroll to top to show success message
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                    this.errorMessage = response.data.message || 'Failed to submit transaction';
                }

            } catch (error) {
                console.error('Submission Error:', error);

                if (error.response) {
                    if (error.response.status === 422) {
                        this.errorMessage = error.response.data.message || 'Invalid transaction message format';
                    } else if (error.response.status === 409) {
                        this.errorMessage = error.response.data.message || 'This transaction has already been submitted or processed';
                    } else {
                        this.errorMessage = error.response.data?.message || 'Failed to submit transaction. Please try again.';
                    }
                } else if (error.request) {
                    this.errorMessage = 'Network error. Please check your connection and try again.';
                } else {
                    this.errorMessage = 'An unexpected error occurred. Please try again.';
                }
            } finally {
                this.isSubmitting = false;
            }
        },

        async fetchRecentSubmissions() {
            try {
                const store = useUserStore();
                const memberId = store.memberId;

                if (!memberId) return;

                const response = await api.get(
                    `/api/pending-deposits/${memberId}/submissions`
                );

                this.recentSubmissions = response.data.submissions || [];
            } catch (error) {
                console.error('Failed to fetch submissions:', error);
            }
        },

        resetForm() {
            this.transactionMessage = '';
            this.transactionType = '';
            this.notes = '';
            this.errorMessage = '';
        },

        formatDate(dateString) {
            return new Date(dateString).toLocaleString();
        },

        formatAmount(amount) {
            return parseFloat(amount).toLocaleString('en-KE', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        },

        getStatusBadgeClass(status) {
            const classes = {
                'pending': 'bg-warning text-dark',
                'processing': 'bg-info text-white',
                'completed': 'bg-success text-white',
                'rejected': 'bg-danger text-white',
                'duplicate': 'bg-secondary text-white'
            };
            return classes[status.toLowerCase()] || 'bg-secondary';
        }
    }
};
</script>